import { Component, Inject, inject, signal } from '@angular/core';
import { PlatformLocation as AiPlatformLocation } from '../../../../core';
import { PLATFORM_LOCATION_TOKEN } from '../../../../core/tokens';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
  FileInfo,
  GALAXY_UPLOADER_SERVICE_TOKEN,
  GalaxyUploaderModule,
} from '@vendasta/galaxy/uploader';
import { ImageUploadService } from '../image-upload.service';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { firstValueFrom } from 'rxjs';
import { first } from 'rxjs/operators';

const LIBRARY_IMAGES = [
  'custom-image-library1.jpg',
  'custom-image-library2.jpg',
  'custom-image-library3.jpg',
  'custom-image-library4.jpg',
  'custom-image-library5.jpg',
  'custom-image-library6.jpg',
  'custom-image-library7.jpg',
  'custom-image-library8.jpg',
  'custom-image-library9.jpg',
  'custom-image-library10.jpg',
  'custom-image-library11.jpg',
  'custom-image-library12.jpg',
  'custom-image-library13.jpg',
  'custom-image-library14.jpg',
  'custom-image-library15.jpg',
  'custom-image-library16.jpg',
  'custom-image-library17.jpg',
  'custom-image-library18.jpg',
  'custom-image-library19.jpg',
  'custom-image-library20.jpg',
  'custom-image-library21.jpg',
  'custom-image-library22.jpg',
  'custom-image-library23.jpg',
  'custom-image-library24.jpg',
  'custom-image-library25.jpg',
  'custom-image-library26.jpg',
  'custom-image-library27.jpg',
  'custom-image-library28.jpg',
  'custom-image-library29.jpg',
  'custom-image-library30.jpg',
  'custom-image-library31.jpg',
  'custom-image-library32.jpg',
  'custom-image-library33.jpg',
  'custom-image-library34.jpg',
  'custom-image-library35.jpg',
];

@Component({
  selector: 'ai-image-upload-modal',
  imports: [MatDialogModule, GalaxyUploaderModule, TranslateModule, GalaxyAvatarModule, MatButtonModule, MatIconModule],
  templateUrl: './image-upload-modal.component.html',
  styleUrl: './image-upload-modal.component.scss',
  providers: [{ provide: GALAXY_UPLOADER_SERVICE_TOKEN, useClass: ImageUploadService }],
})
export class ImageUploadModalComponent {
  protected readonly file = signal<FileInfo | null>(null);
  private readonly uploadService = inject(GALAXY_UPLOADER_SERVICE_TOKEN) as ImageUploadService;
  protected readonly state = signal('change');
  private readonly dialogRef = inject(MatDialogRef<ImageUploadModalComponent>);
  private readonly platformLocation = inject(PLATFORM_LOCATION_TOKEN);

  readonly libraryImages = LIBRARY_IMAGES.map((filename) => ({
    filename,
    url: `${this.getAssetBaseUrl()}/image-library/${filename}`,
  }));

  private getAssetBaseUrl(): string {
    if (window.location.hostname === 'localhost') {
      return '/assets/ai-assistant';
    }
    
    switch (this.platformLocation) {
      case AiPlatformLocation.PLATFORM_LOCATION_PARTNER_CENTER:
        return 'https://vstatic-prod.apigateway.co/partner-center-client.9223372035087392964.prod/assets/ai-assistant';
      case AiPlatformLocation.PLATFORM_LOCATION_BUSINESS_APP:
      default:
        return 'https://vstatic-prod.apigateway.co/business-center-client/assets/ai-assistant';
    }
  }

  constructor(@Inject(MAT_DIALOG_DATA) public data: { imageURL: string; defaultSvg: string }) {
    this.state.set(data.imageURL ? 'view' : 'change');
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectedFile = input.files?.[0];
    if (selectedFile) {
      this.uploadService.addFile(selectedFile);
      
      this.uploadService.files$$.pipe(first()).subscribe((files) => {
        const fileInfo = files.find(f => f.name === selectedFile.name);
        if (fileInfo) {
          this.file.set(fileInfo);
        }
      });
    }
    input.value = '';
  }

  selectLibraryImage(imageUrl: string): void {
    this.dialogRef.close({ newImageUrl: imageUrl });
  }

  onRemove(): void {
    this.dialogRef.close({ removed: true });
  }

  async endEdit(blob?: Blob | null): Promise<void> {
    const currentFile = this.file();
    if (blob && currentFile) {
      this.uploadService.setCroppedImage(currentFile, blob);
      this.uploadService.uploadQueuedFiles();

      firstValueFrom(this.uploadService.fileUploaded$).then((fileInfo) => {
        this.dialogRef.close({
          newImageUrl: fileInfo.url,
        });
      });
    } else {
      this.uploadService.clear();
      this.file.set(null);
      this.dialogRef.close();
    }
  }
}
